import { Result } from "antd";
import { motion } from "framer-motion";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { PuffLoader } from "react-spinners";
import { Toaster } from "react-hot-toast";

// Components & Utilities
import Colors from "./helpers/Colors";
import { useWindowSize } from "./hooks/useWindowSize";
import { routing } from "./routes/Routing";
import PushNotification from "./components/firebaseNotifications/PushNotification";
import { requestForToken } from "./components/firebaseNotifications/firebase";

// Lazy loaded components
const Login = lazy(() => import("./interface/pages/auth/Login"));
const Verify = lazy(() => import("./interface/pages/auth/Verify"));
const VerifyLoginComp = lazy(() => import("./components/VerifyLoginComp"));
const MainLayout = lazy(() => import("./interface/layouts/MainLayout"));
const Page404 = lazy(() => import("./interface/result/Page404"));
const Page403 = lazy(() => import("./interface/result/Page403"));
const Page500 = lazy(() => import("./interface/result/Page500"));
const PageBadRequest = lazy(() => import("./interface/result/PageBadRequest"));
const PageMaintenance = lazy(() => import("./interface/result/PageMaintenance"));
const PageCheckData = lazy(() => import("./interface/result/PageCheckData"));

interface RouteProps {
  path: string;
  element: React.ReactNode;
}

// Loading component
const LoadingSpinner: React.FC = () => (
  <div className="flex items-center justify-center h-screen">
    <PuffLoader color={Colors.primary} />
  </div>
);

// Desktop error message component
const DesktopError: React.FC = () => (
  <div className="flex flex-col items-center justify-center h-screen p-4">
    <Result status="403" title="Sorry, this app is not available on desktop." subTitle="Please use this app on a mobile device." />
  </div>
);

// Routes component with animations
const AnimatedRoutes: React.FC = () => {
  const location = useLocation();

  return (
    <motion.div key={location.pathname}>
      <Routes location={location}>
        {/* Error & Utility Pages */}
        <Route path="*" element={<Page404 />} />
        <Route path="/page403" element={<Page403 />} />
        <Route path="/page500" element={<Page500 />} />
        <Route path="/maintenance" element={<PageMaintenance />} />
        <Route path="/bad-request" element={<PageBadRequest />} />
        <Route path="/check-data-orawt" element={<PageCheckData />} />

        {/* Auth Routes */}
        <Route element={<VerifyLoginComp />}>
          <Route path="/login" element={<Login />} />
          <Route path="/verify" element={<Verify />} />
        </Route>

        {/* Main App Routes */}
        <Route path="/" element={<MainLayout />}>
          {routing.map(({ path, element }: RouteProps, index: number) => (
            <Route key={`route-${path}-${index}`} path={path} element={element} />
          ))}
        </Route>
      </Routes>
    </motion.div>
  );
};

// Main App component
const App: React.FC = () => {
  const [size] = useWindowSize();
  const isMobile = size <= 480;

  useEffect(() => {
    const token = localStorage.getItem("fcmToken");
    if (token) {
      requestForToken();
    }
  }, []);

  if (!isMobile) {
    return <DesktopError />;
  }

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <BrowserRouter>
        <PushNotification />
        <AnimatedRoutes />
        <Toaster position="top-center" />
      </BrowserRouter>
    </Suspense>
  );
};

export default App;
